//#region --- Promo Report Search Request DTO ---

import { Type } from "class-transformer";
import { IAdvanceGridSearch } from "./kendo-grid";
import { DateHelper, dateOnly, startOfMonth } from "../helpers";

export class PromoReportSearchRequestDto {
  // Following three is just for forecast
  is_forecast_data : boolean = false;
  filter_on_exhausted : boolean = false;
  exhausted : boolean = false;

  promo_discount_ids?: string[];
  promo_price_reduction_type_id?: number;
  promo_category_id?: number;
  service_ids?: string[];
  invoice_numbers?: string[];
  purchase_status_ids?: number[];
  purchase_ids?: string[];
  promo_name_reference?: string = '';
  @Type(() => Date)
  summary_start_date?: Date;
  @Type(() => Date)
  summary_end_date?: Date;
  page_no?: number;
  page_size?: number;

  // Invoice Number
  get invoice_number(): string {
    if (this.invoice_numbers && this.invoice_numbers.length > 0)
      return this.invoice_numbers[0];

    return '';
  }

  set invoice_number(value: string) {
    if (!this.invoice_numbers)
      this.invoice_numbers = [value];
    else
      this.invoice_numbers[0] = value;
  }

  get service_id(): string {
    if (this.service_ids && this.service_ids.length > 0)
      return this.service_ids[0];

    return null;
  }

  set service_id(value: string) {

    if (!value) {
      this.service_ids = [];
      return;
    }
    else
      this.service_ids = [value];
  }

  constructor(init?: Partial<PromoReportSearchRequestDto>) {
    Object.assign(this, init);
  }

  public clearFilters(): void {
    this.is_forecast_data = false;
    this.filter_on_exhausted = false;
    this.exhausted = false;

    this.promo_discount_ids = [];
    this.promo_price_reduction_type_id = null;
    this.promo_category_id = null;
    this.service_ids = [];
    this.invoice_numbers = [];
    this.purchase_status_ids = [];
    this.purchase_ids = [];
    this.promo_name_reference = '';
    this.page_no = null;
    this.page_size = null;
    // this.summary_start_date = null;
    // this.summary_end_date = null;
    this.setDefaultDateRange();
  }

  private setDefaultDateRange(): void {
      const currentDate = new Date();
      this.summary_start_date = startOfMonth(currentDate);
      this.summary_end_date = dateOnly(currentDate);
  }
}

export class PromoReportSummaryAdvSearchFilter extends PromoReportSearchRequestDto implements IAdvanceGridSearch{
}

//#endregion --- Promo Report Search Request DTO ---

//#region --- Promo Report Data DTOs ---
export class PromoSummaryDto {
  promo_discount_id: string;
  promo_name: string;
  promo_price_reduction_name: string;
  promo_discount_category_id: number;
  promo_discount_category_name: string;
  product_price: number;
  promo_adjustment: number;
  applied_promo_adjustment: number;
  final_amount: number;
  reported_usage_count: number;               // Usage count of the promo discount for the report period
  total_usage_count: number;                  // Total usage count of the promo discount
  remaining_usage_count: number;              // Estimated remaining usage count of the promo discount

  promo_status_id: number;
  promo_status_name: string;
  promo_status_class?: string;                // Used only by Front-end

  promo_start_date: string;
  promo_end_date?: string;
	promo_billing_frequency_id?: number | null;
	promo_billing_frequency_name?: string | null;
	promo_reduction_type_id?: number | null;
  promo_reduction_type_name?: string | null;
}

export class PromoSummaryServiceDto extends PromoSummaryDto {
  service_id: string;
  customer_full_name: string;
  customer_email: string;
  product_name: string;
  service_status_name: string;
  area_name: string;

  exhaustion_reason: string;
  @Type(() => Date)
  activation_date: Date;
  @Type(() => Date)
  exhaustion_date?: Date;
  can_remove: boolean = false;
  usage_count: number = 1;
  @Type(() => Date)
  exhaust_creation_date?: Date = new Date();
  @Type(() => Date)
  exhaust_effective_date: Date = new Date();
}

export class PromoSummaryServicePurchaseDto extends PromoSummaryServiceDto {
  @Type(() => Date)
  purchase_date: Date;
  invoice_number : string;
  purchase_id: string;

  purchase_status_id: number;
  purchase_status_name: string;

  // Additional fields for purchase summary
  list_price : number;
  final_price : number;
}

export class PromoServicePurchaseInfoDto {
  purchase_id: string;
  @Type(() => Date)
  purchase_date: Date;
  total_amount: number;
  purchase_status_id: number;
  purchase_status_name: string;
  promo_purchase_lines: PromoServicePurchaseDetailLineDto[] = [];

  get displayPurchaseDate(): string {
    return this.purchase_date ? DateHelper.formatDate(this.purchase_date, '/') : '';
  }
}

export class PromoDiscountFilterListRequestDto {
	include_deleted: boolean = false;
	include_expired: boolean = true;
}

export class PromoSummaryNestedInvoiceDto {
  invoice_number: string;
  @Type(() => Date)
  purchase_date: Date;
  purchase_id: string;
  amount: number;
  purchase_status_id: number;
  purchase_status_name: string;
  product_price: number;
  promo_adjustment: number;
  applied_promo_adjustment: number;
  final_amount: number;
  total_usage_count: number;
}

export class PromoSummaryServiceWithDataDto extends PromoSummaryServiceDto {
  @Type(() => PromoSummaryNestedInvoiceDto)
  invoices : PromoSummaryNestedInvoiceDto[] = [];
}

export class PromoSummaryWithDataDto extends PromoSummaryDto {
  @Type(() => PromoSummaryServiceWithDataDto)
  services : PromoSummaryServiceWithDataDto[];

  get service() : PromoSummaryServiceWithDataDto {
    if (this.services && this.services.length > 0)
      return this.services[0];

    return null;
  }

  get comment(): string {
    return this.service?.exhaustion_reason || '';
  }
  get comment_creation_date(): Date | null {
    return this.service?.exhaust_creation_date || null;
  }
  get comment_effective_date(): Date | null {
    return this.service?.exhaust_effective_date || null;
  }

  get activation_date(): Date {
    return this.service?.activation_date || null;
  }

  get exhaustion_date(): Date {
    return this.service?.exhaustion_date || null;
  }

  get can_remove(): boolean {
    return this.service?.can_remove || false;
  }

  set can_remove(value: boolean) {
    this.service.can_remove = value;
  }

}

export class PromoServicePurchaseDetailLineDto {
  purchase_line_id: string;
  description: string;
  quantity: number;
  amount_per_quantity: number;
  amount: number;
  purchase_line_type_id: number;
  purchase_line_type_name: string;
  promo_discount_id?: string;
  promo_discount_name?: string;
  is_hidden: boolean;
}

//#endregion --- Promo Report Data DTOs ---

export class PromoReportSummaryDataExportRequestDto {
  public format: string = 'csv';
  public selected_field_names: string[] = [];
  public filter? : PromoReportSearchRequestDto = null;
}

export class PromoSummaryServiceQueryParamsBase {
  @Type(() => PromoReportSearchRequestDto)
  public promoReportSearchRequestDto: PromoReportSearchRequestDto;
}

export class PromoSummaryPromotionQueryParams extends PromoSummaryServiceQueryParamsBase {
}

export class PromoSummaryServiceQueryParams extends PromoSummaryServiceQueryParamsBase {
  @Type(() => PromoSummaryDto)
  public promoSummaryDto? : PromoSummaryDto;
}

export class PromoSummaryServicePurchaseQueryParams extends PromoSummaryServiceQueryParamsBase {
  @Type(() => PromoSummaryServiceDto)
  public promoSummaryServiceDto? : PromoSummaryServiceDto;
}

export class PromoSummaryServicePurchaseInfoQueryParams extends PromoSummaryServiceQueryParamsBase {
  @Type(() => PromoSummaryServicePurchaseDto)
  public promoSummaryServicePurchaseDto? : PromoSummaryServicePurchaseDto;
}

export class EligibilityResponseServiceDto {
  public id: string;
  public collector_id: string;
  public customer_id: string;
  public product_id: string;
  public area_id: string;
  public last_known_status_id: number;
  @Type(() => Date)
  public application_date: Date;
  @Type(() => Date)
  public effective_date: Date;
  @Type(() => Date)
  public cancellation_date: Date;
  public in_arrears: boolean;
  @Type(() => Date)
  public period_end_date: Date;
  @Type(() => Date)
  public suspension_date: Date;
}

export class EligibilityResponseDto {
  public service : EligibilityResponseServiceDto;
  // Dont need the promo_discount object
}
